import { useQuery } from '@apollo/client/react';
import { BugReport, Flag, PlaylistAdd } from '@mui/icons-material';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import DefaultIcon from '@mui/icons-material/ViewList';
import React from 'react';
import { Layout, Menu, useResourceDefinitions } from 'react-admin';

import { SelfDocument } from '../../graphql/types';
import { SplitFlags, useFeatureFlags } from '../../hooks/useFeatureFlags';
import { EnvLevel, getEnvLevel } from '../../utils/getEnvLevel';
import AuthLayout from '../AuthLayout';
import {
  EXERCISE_SUBMENU_RESOURCE_NAMES,
  ExercisesSubmenu,
} from './ExercisesSubmenu';

const exerciseSubmenuResourceNameSet = new Set<string>(
  EXERCISE_SUBMENU_RESOURCE_NAMES,
);

export const CustomMenu: React.FC = (props) => {
  const { flags } = useFeatureFlags();

  const enableTestDataService = flags[SplitFlags.SPA_TEST_DATA_SERVICE];
  const enableLaunchManager = flags[SplitFlags.SPA_LAUNCH_MANAGER];
  const resourcesDefinitions = useResourceDefinitions();
  const resources = Object.keys(resourcesDefinitions).map(
    (name) => resourcesDefinitions[name],
  );
  const listResources = resources.filter((resource) => resource.hasList);

  const exerciseSubmenuResourcesInOrder = listResources
    .filter((resource) => exerciseSubmenuResourceNameSet.has(resource.name))
    .sort((a, b) => {
      const indexA = EXERCISE_SUBMENU_RESOURCE_NAMES.indexOf(
        a.name as (typeof EXERCISE_SUBMENU_RESOURCE_NAMES)[number],
      );
      const indexB = EXERCISE_SUBMENU_RESOURCE_NAMES.indexOf(
        b.name as (typeof EXERCISE_SUBMENU_RESOURCE_NAMES)[number],
      );
      return indexA - indexB;
    });

  const { loading, data: selfQueryResponse } = useQuery(SelfDocument);

  if (loading && !selfQueryResponse?.self) {
    return <AuthLayout />;
  }

  const isAdmin = Boolean(selfQueryResponse?.self?.isAdmin);
  const isExerciseEditor = Boolean(selfQueryResponse?.self?.isExerciseEditor);
  const isTestDataCreator = Boolean(selfQueryResponse?.self?.isTestDataCreator);
  const showExerciseSubmenu = isAdmin || isExerciseEditor;
  const showTestDataMenuItem =
    (isAdmin || isTestDataCreator) &&
    (getEnvLevel() === EnvLevel.DEV || getEnvLevel() === EnvLevel.TESTING) &&
    enableTestDataService;

  let exerciseSubmenuRendered = false;
  return (
    <Menu {...props}>
      {listResources.map((resource) => {
        const isExerciseSubmenuResource = exerciseSubmenuResourceNameSet.has(
          resource.name,
        );

        if (isExerciseSubmenuResource) {
          if (!showExerciseSubmenu) {
            return null;
          }
          if (!exerciseSubmenuRendered) {
            exerciseSubmenuRendered = true;
            return (
              <ExercisesSubmenu
                key="exercises-submenu"
                resources={exerciseSubmenuResourcesInOrder}
                defaultExpanded={!isAdmin}
              />
            );
          }
          return null;
        }
        return (
          <Menu.ResourceItem
            name={resource.name}
            key={resource.name}
            primaryText={resource.options?.label ?? resource.name}
            leftIcon={resource.icon ? <resource.icon /> : <DefaultIcon />}
          />
        );
      })}
      {/* C U S T O M  R O U T E S */}
      {showTestDataMenuItem && (
        <Menu.Item
          primaryText="Test Data"
          to="/app/testData"
          leftIcon={<PlaylistAdd />}
        />
      )}
      {isAdmin && enableLaunchManager && (
        <Menu.Item
          primaryText="Launch Manager"
          to="/app/launchManager"
          leftIcon={<DefaultIcon />}
        />
      )}
      {/* An array, not a Fragment: MUI's MenuList rejects Fragment children */}
      {selfQueryResponse?.self?.isAdmin && [
        <Menu.Item
          key="emailDebugger"
          primaryText="Email Debugger"
          to="/app/emailDebugger"
          leftIcon={<BugReport />}
        />,
        <Menu.Item
          key="featureFlagState"
          primaryText="Feature Flag Status"
          to="/app/FeatureFlagState"
          leftIcon={<Flag />}
        />,
        <Menu.Item
          key="systemPreferences"
          primaryText="System Preferences"
          to="/app/systemPreferences"
          leftIcon={<SettingsApplicationsIcon />}
        />,
      ]}
    </Menu>
  );
};

export const CustomLayout: React.FC<React.PropsWithChildren> = ({
  children,
  ...rest
}) => (
  <Layout {...rest} menu={CustomMenu}>
    {children}
  </Layout>
);
